import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
	Box,
	Typography,
	Select,
	MenuItem,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Paper,
	CircularProgress,
	Alert,
	AlertTitle,
	Chip,
	FormControl,
	InputLabel,
	SelectChangeEvent,
	TextField,
	InputAdornment,
	IconButton,
	LinearProgress,
} from '@mui/material';
import {
	PlayArrow as PlayArrowIcon,
	Refresh as RefreshIcon,
	Clear as ClearIcon,
	FilterList as FilterListIcon,
	ExpandMore as ExpandMoreIcon,
	ExpandLess as ExpandLessIcon,
	CheckCircle as CheckCircleIcon,
	Error as ErrorIcon,
	HourglassEmpty as PendingIcon,
	Loop as RunningIcon,
	SkipNext as SkipIcon,
} from '@mui/icons-material';
import { apiService, AnalysisRun, AnalysisStep } from '../services/api';

const RUN_OVERVIEW_KEY = '_run_overview';
const FIRST_DATA_TYPE = 'packages';

const RESULT_TYPE_LABELS: Record<string, string> = {
	[RUN_OVERVIEW_KEY]: 'Run Overview',
	packages: 'Packages',
	flows: 'Flows',
	objects: 'Objects',
	record_types: 'Record Types',
	profiles: 'Profiles',
};

const RESULT_TYPES = Object.keys(RESULT_TYPE_LABELS);

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
	pending: 'default',
	running: 'info',
	completed: 'success',
	completed_with_errors: 'warning',
	failed: 'error',
};

const STEP_STATUS_CONFIG: Record<string, { color: 'default' | 'info' | 'success' | 'warning' | 'error'; icon: React.ReactElement }> = {
	pending: { color: 'default', icon: <PendingIcon fontSize="small" /> },
	running: { color: 'info', icon: <RunningIcon fontSize="small" /> },
	completed: { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
	failed: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
	skipped: { color: 'warning', icon: <SkipIcon fontSize="small" /> },
};

function formatStepName(name: string): string {
	return name
		.replace(/_/g, ' ')
		.replace(/\b\w/g, c => c.toUpperCase());
}

function parseErrorInfo(errorInfo: string | null): { message: string; stack?: string } | null {
	if (!errorInfo) return null;
	try {
		const parsed = JSON.parse(errorInfo);
		return { message: parsed.message || errorInfo, stack: parsed.stack };
	} catch {
		return { message: errorInfo };
	}
}

/** Columns to hide from the auto-generated table (IDs, foreign keys, large blobs) */
const HIDDEN_COLUMNS = new Set([
	'id',
	'run_id',
	'step_id',
	'org_id',
	'snapshot_id',
	'raw_metadata_json',
]);

function formatColumnHeader(key: string): string {
	return key
		.replace(/_/g, ' ')
		.replace(/\bjson\b/gi, 'JSON')
		.replace(/\bapi\b/gi, 'API')
		.replace(/\burl\b/gi, 'URL')
		.replace(/\bid\b/gi, 'ID')
		.replace(/\b\w/g, c => c.toUpperCase());
}

function formatCellValue(value: unknown): string {
	if (value === null || value === undefined) return '—';
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (typeof value === 'object') return JSON.stringify(value);
	return String(value);
}

interface AnalysisProps {
	snapshotId: string;
	workspaceId: string;
}

export const Analysis: React.FC<AnalysisProps> = ({ snapshotId, workspaceId }) => {
	const [runs, setRuns] = useState<AnalysisRun[]>([]);
	const [selectedRunId, setSelectedRunId] = useState<string>('');
	const [selectedResultType, setSelectedResultType] = useState<string>(RESULT_TYPES[0]);
	const [results, setResults] = useState<Record<string, any>[]>([]);
	const [runsLoading, setRunsLoading] = useState(false);
	const [resultsLoading, setResultsLoading] = useState(false);
	const [creating, setCreating] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Step details state
	const [steps, setSteps] = useState<AnalysisStep[]>([]);
	const [stepsLoading, setStepsLoading] = useState(false);
	const [expandedStepError, setExpandedStepError] = useState<string | null>(null);

	const isRunOverview = selectedResultType === RUN_OVERVIEW_KEY;

	// Sort & filter state
	type SortDirection = 'asc' | 'desc';
	const [sortColumn, setSortColumn] = useState<string | null>(null);
	const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
	const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
	const [showFilters, setShowFilters] = useState(false);

	const selectResultTypeForRun = useCallback((run: AnalysisRun) => {
		if (run.status === 'completed') {
			setSelectedResultType(FIRST_DATA_TYPE);
		} else {
			setSelectedResultType(RUN_OVERVIEW_KEY);
		}
	}, []);

	// Fetch analysis runs
	const loadRuns = useCallback(async () => {
		setRunsLoading(true);
		setError(null);
		try {
			const response = await apiService.getAnalysisRuns(snapshotId);
			if (response.success && response.data) {
				const sorted = [...response.data.runs].sort(
					(a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
				);
				setRuns(sorted);
				if (sorted.length > 0 && !selectedRunId) {
					setSelectedRunId(sorted[0].run_id);
					selectResultTypeForRun(sorted[0]);
				}
			} else {
				setError(response.error || 'Failed to load analysis runs');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load analysis runs');
		} finally {
			setRunsLoading(false);
		}
	}, [snapshotId, selectedRunId, selectResultTypeForRun]);

	// Fetch results for the selected run + type
	const loadResults = useCallback(async () => {
		if (!selectedRunId || !selectedResultType || selectedResultType === RUN_OVERVIEW_KEY) {
			setResults([]);
			return;
		}
		setResultsLoading(true);
		setError(null);
		try {
			const response = await apiService.getAnalysisResults(selectedRunId, selectedResultType);
			if (response.success && response.data) {
				setResults(response.data.results);
			} else {
				setResults([]);
				setError(response.error || 'Failed to load results');
			}
		} catch (err) {
			setResults([]);
			setError(err instanceof Error ? err.message : 'Failed to load results');
		} finally {
			setResultsLoading(false);
		}
	}, [selectedRunId, selectedResultType]);

	// Fetch step details for a run (when Run Overview is selected)
	const loadSteps = useCallback(async () => {
		if (!selectedRunId || selectedResultType !== RUN_OVERVIEW_KEY) {
			setSteps([]);
			return;
		}
		setStepsLoading(true);
		try {
			const response = await apiService.getAnalysisRun(selectedRunId);
			if (response.success && response.data) {
				setSteps(response.data.steps);
			}
		} catch {
			// Non-critical — don't block the UI
		} finally {
			setStepsLoading(false);
		}
	}, [selectedRunId, selectedResultType]);

	// Load runs on mount
	useEffect(() => {
		loadRuns();
	}, [loadRuns]);

	// Load results when run or type changes
	useEffect(() => {
		loadResults();
	}, [loadResults]);

	// Load step details when Run Overview is active
	useEffect(() => {
		loadSteps();
	}, [loadSteps]);

	const handleRefresh = useCallback(() => {
		loadRuns();
		loadSteps();
		loadResults();
	}, [loadRuns, loadSteps, loadResults]);

	const selectedRun = runs.find(r => r.run_id === selectedRunId);
	const isRunActive = selectedRun?.status === 'pending' || selectedRun?.status === 'running';

	// Auto-poll every 5 s while the selected run is in a non-terminal state
	useEffect(() => {
		if (!isRunActive) return;
		const intervalId = setInterval(handleRefresh, 5000);
		return () => clearInterval(intervalId);
	}, [isRunActive, handleRefresh]);

	// Create a new analysis run
	const handleCreateRun = async () => {
		setCreating(true);
		setError(null);
		try {
			const response = await apiService.createAnalysisRun(snapshotId, workspaceId);
			if (response.success && response.data) {
				// Refresh the runs list and select the new one
				const runsResponse = await apiService.getAnalysisRuns(snapshotId);
				if (runsResponse.success && runsResponse.data) {
					const sorted = [...runsResponse.data.runs].sort(
						(a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
					);
					setRuns(sorted);
					setSelectedRunId(response.data.runId);
					setSelectedResultType(RUN_OVERVIEW_KEY);
				}
			} else {
				setError(response.error || 'Failed to create analysis run');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to create analysis run');
		} finally {
			setCreating(false);
		}
	};

	const handleRunChange = (event: SelectChangeEvent<string>) => {
		const runId = event.target.value;
		setSelectedRunId(runId);
		setResults([]);
		const run = runs.find(r => r.run_id === runId);
		if (run) {
			selectResultTypeForRun(run);
		}
	};

	const handleResultTypeChange = (event: SelectChangeEvent<string>) => {
		setSelectedResultType(event.target.value);
		setResults([]);
		setSortColumn(null);
		setSortDirection('asc');
		setColumnFilters({});
	};

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			if (sortDirection === 'asc') {
				setSortDirection('desc');
			} else {
				setSortColumn(null);
				setSortDirection('asc');
			}
		} else {
			setSortColumn(column);
			setSortDirection('asc');
		}
	};

	const handleFilterChange = (column: string, value: string) => {
		setColumnFilters(prev => {
			const next = { ...prev };
			if (value) {
				next[column] = value;
			} else {
				delete next[column];
			}
			return next;
		});
	};

	const clearAllFilters = () => {
		setColumnFilters({});
	};

	// Build table columns from the first result row, excluding hidden columns
	const columns = results.length > 0
		? Object.keys(results[0]).filter(key => !HIDDEN_COLUMNS.has(key))
		: [];

	const activeFilterCount = Object.keys(columnFilters).length;

	const processedResults = useMemo(() => {
		let data = results;

		// Apply column text filters
		if (activeFilterCount > 0) {
			data = data.filter(row =>
				Object.entries(columnFilters).every(([col, filterText]) => {
					const cellValue = formatCellValue(row[col]).toLowerCase();
					return cellValue.includes(filterText.toLowerCase());
				}),
			);
		}

		// Apply sorting
		if (sortColumn) {
			data = [...data].sort((a, b) => {
				const aVal = a[sortColumn];
				const bVal = b[sortColumn];

				if (aVal == null && bVal == null) return 0;
				if (aVal == null) return sortDirection === 'asc' ? -1 : 1;
				if (bVal == null) return sortDirection === 'asc' ? 1 : -1;

				if (typeof aVal === 'number' && typeof bVal === 'number') {
					return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
				}
				if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
					return sortDirection === 'asc'
						? Number(aVal) - Number(bVal)
						: Number(bVal) - Number(aVal);
				}

				const aStr = String(aVal).toLowerCase();
				const bStr = String(bVal).toLowerCase();
				const cmp = aStr.localeCompare(bStr);
				return sortDirection === 'asc' ? cmp : -cmp;
			});
		}

		return data;
	}, [results, columnFilters, activeFilterCount, sortColumn, sortDirection]);

	return (
		<Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 3, minHeight: 0, overflow: 'hidden' }}>
			{/* Header */}
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
				<Typography variant="h4" sx={{ fontWeight: 'bold' }}>
					Analysis
				</Typography>
				<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
					{isRunActive ? (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
							<CircularProgress size={14} />
							<Typography variant="caption" color="text.secondary">
								Auto-refreshing
							</Typography>
						</Box>
					) : (
						<Button
							variant="outlined"
							size="small"
							startIcon={<RefreshIcon />}
							onClick={handleRefresh}
							disabled={runsLoading || stepsLoading || resultsLoading}
						>
							Refresh
						</Button>
					)}
					<Button
						variant="contained"
						size="small"
						startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
						onClick={handleCreateRun}
						disabled={creating}
					>
						{creating ? 'Starting...' : 'New Analysis Run'}
					</Button>
				</Box>
			</Box>

			{error && (
				<Alert severity="error" onClose={() => setError(null)}>
					{error}
				</Alert>
			)}

			{/* Controls row */}
			<Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
				{/* Run selector */}
				<FormControl size="small" sx={{ minWidth: 350 }}>
					<InputLabel id="analysis-run-label">Analysis Run</InputLabel>
					<Select
						labelId="analysis-run-label"
						value={selectedRunId}
						label="Analysis Run"
						onChange={handleRunChange}
						disabled={runsLoading || runs.length === 0}
					>
						{runs.map(run => (
							<MenuItem key={run.run_id} value={run.run_id}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<Chip
										label={run.status}
										size="small"
										color={STATUS_COLORS[run.status] || 'default'}
										sx={{ fontSize: '0.7rem', height: 20 }}
									/>
									<Typography variant="body2">
										{new Date(run.started_at).toLocaleString()}
									</Typography>
								</Box>
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{/* Result type selector */}
				<FormControl size="small" sx={{ minWidth: 220 }}>
					<InputLabel id="result-type-label">Result Type</InputLabel>
					<Select
						labelId="result-type-label"
						value={selectedResultType}
						label="Result Type"
						onChange={handleResultTypeChange}
						disabled={!selectedRunId}
					>
						{RESULT_TYPES.map(type => (
							<MenuItem key={type} value={type}>
								{RESULT_TYPE_LABELS[type]}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{/* Status indicator */}
				{selectedRun && (
					<Chip
						label={`Run: ${selectedRun.status}`}
						color={STATUS_COLORS[selectedRun.status] || 'default'}
						size="small"
					/>
				)}

			{!isRunOverview && results.length > 0 && (
				<>
					<Button
						variant={showFilters ? 'contained' : 'outlined'}
						size="small"
						startIcon={<FilterListIcon />}
						onClick={() => setShowFilters(prev => !prev)}
						color={activeFilterCount > 0 ? 'primary' : 'inherit'}
					>
						Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
					</Button>
					{activeFilterCount > 0 && (
						<Button size="small" onClick={clearAllFilters}>
							Clear filters
						</Button>
					)}
					<Typography variant="body2" color="text.secondary">
						{processedResults.length === results.length
							? `${results.length} result${results.length !== 1 ? 's' : ''}`
							: `${processedResults.length} of ${results.length} results`}
					</Typography>
				</>
			)}
			</Box>

		{/* Main content area */}
		<Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
			{runsLoading && runs.length === 0 ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
					<CircularProgress />
				</Box>
			) : runs.length === 0 ? (
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 6, gap: 2 }}>
					<Typography variant="h6" color="text.secondary">
						No analysis runs yet
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Click "New Analysis Run" to analyze the current metadata snapshot.
					</Typography>
				</Box>
			) : isRunOverview ? (
				/* ── Run Overview view ── */
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}>
					{/* Status banner */}
					{selectedRun && (() => {
						const severity: 'error' | 'warning' | 'info' | 'success' =
							selectedRun.status === 'failed' ? 'error'
							: selectedRun.status === 'completed_with_errors' ? 'warning'
							: selectedRun.status === 'running' || selectedRun.status === 'pending' ? 'info'
							: 'success';

						const title =
							selectedRun.status === 'failed' ? 'Analysis Run Failed'
							: selectedRun.status === 'completed_with_errors' ? 'Analysis Run Completed with Errors'
							: selectedRun.status === 'running' ? 'Analysis Run In Progress'
							: selectedRun.status === 'pending' ? 'Analysis Run Pending'
							: 'Analysis Run Completed';

						const runError = parseErrorInfo(selectedRun.error_info);
						let description: string;
						if (runError) {
							description = runError.message;
						} else if (steps.length > 0) {
							const counts = {
								completed: steps.filter(s => s.status === 'completed').length,
								failed: steps.filter(s => s.status === 'failed').length,
								skipped: steps.filter(s => s.status === 'skipped').length,
								running: steps.filter(s => s.status === 'running').length,
								pending: steps.filter(s => s.status === 'pending').length,
							};
							const parts: string[] = [];
							if (counts.completed > 0) parts.push(`${counts.completed} completed`);
							if (counts.running > 0) parts.push(`${counts.running} running`);
							if (counts.failed > 0) parts.push(`${counts.failed} failed`);
							if (counts.skipped > 0) parts.push(`${counts.skipped} skipped`);
							if (counts.pending > 0) parts.push(`${counts.pending} pending`);
							description = `${parts.join(', ')} out of ${steps.length} steps.`;
						} else if (stepsLoading) {
							description = 'Loading step details...';
						} else {
							description = selectedRun.status === 'running'
								? 'Waiting for step data...'
								: 'No step data available.';
						}

						return (
							<Alert severity={severity} sx={{ flexShrink: 0 }}>
								<AlertTitle>{title}</AlertTitle>
								{description}
							</Alert>
						);
					})()}

					{/* Step details table */}
					{stepsLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
							<CircularProgress />
						</Box>
					) : steps.length === 0 ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
							<Typography variant="body1" color="text.secondary">
								No step data available for this run.
							</Typography>
						</Box>
					) : (
						<TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100', width: 40 }}>#</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>Step</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100', width: 120 }}>Status</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100', width: 130 }}>Progress</TableCell>
										<TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>Details</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{[...steps]
										.sort((a, b) => a.step_order - b.step_order)
										.map(step => {
											const cfg = STEP_STATUS_CONFIG[step.status] || STEP_STATUS_CONFIG.pending;
											const stepError = parseErrorInfo(step.error_info);
											const hasProgress = step.items_total != null && step.items_total > 0;
											const progressPct = hasProgress
												? Math.round(((step.items_processed || 0) / step.items_total!) * 100)
												: null;
											const isExpanded = expandedStepError === step.step_id;

											return (
												<React.Fragment key={step.step_id}>
													<TableRow
														hover
														sx={{
															cursor: stepError ? 'pointer' : 'default',
															'&:last-child td': { borderBottom: isExpanded ? 0 : undefined },
														}}
														onClick={() => {
															if (stepError) {
																setExpandedStepError(isExpanded ? null : step.step_id);
															}
														}}
													>
														<TableCell sx={{ color: 'text.secondary' }}>{step.step_order}</TableCell>
														<TableCell>{formatStepName(step.step_name)}</TableCell>
														<TableCell>
															<Chip
																icon={cfg.icon}
																label={step.status}
																color={cfg.color}
																size="small"
																sx={{ fontSize: '0.7rem', height: 22 }}
															/>
														</TableCell>
														<TableCell>
															{hasProgress ? (
																<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
																	<LinearProgress
																		variant="determinate"
																		value={progressPct!}
																		color={cfg.color === 'default' ? 'primary' : cfg.color}
																		sx={{ flex: 1, height: 6, borderRadius: 3 }}
																	/>
																	<Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, textAlign: 'right' }}>
																		{step.items_processed}/{step.items_total}
																	</Typography>
																</Box>
															) : (
																<Typography variant="caption" color="text.secondary">—</Typography>
															)}
														</TableCell>
														<TableCell>
															{stepError ? (
																<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
																	<Typography variant="caption" color="error.main" noWrap sx={{ maxWidth: 300 }}>
																		{stepError.message}
																	</Typography>
																	{stepError.stack && (
																		isExpanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />
																	)}
																</Box>
															) : step.status === 'completed' && hasProgress ? (
																<Typography variant="caption" color="success.main">
																	{step.items_processed} items
																</Typography>
															) : null}
														</TableCell>
													</TableRow>
													{stepError?.stack && isExpanded && (
														<TableRow>
															<TableCell colSpan={5} sx={{ py: 0, backgroundColor: 'grey.50' }}>
																<Box
																	component="pre"
																	sx={{
																		m: 1,
																		p: 1.5,
																		fontSize: '0.7rem',
																		fontFamily: 'monospace',
																		overflow: 'auto',
																		maxHeight: 200,
																		backgroundColor: 'grey.900',
																		color: 'grey.100',
																		borderRadius: 1,
																		whiteSpace: 'pre-wrap',
																		wordBreak: 'break-all',
																	}}
																>
																	{stepError.stack}
																</Box>
															</TableCell>
														</TableRow>
													)}
												</React.Fragment>
											);
										})}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Box>
			) : resultsLoading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
					<CircularProgress />
				</Box>
			) : results.length === 0 ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
					<Typography variant="body1" color="text.secondary">
						No results found for this type.
					</Typography>
				</Box>
			) : (
				/* ── Results data table ── */
				<TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								{columns.map(col => (
									<TableCell
										key={col}
										sortDirection={sortColumn === col ? sortDirection : false}
										sx={{
											fontWeight: 'bold',
											whiteSpace: 'nowrap',
											backgroundColor: 'grey.100',
											cursor: 'pointer',
											userSelect: 'none',
										}}
									>
										<TableSortLabel
											active={sortColumn === col}
											direction={sortColumn === col ? sortDirection : 'asc'}
											onClick={() => handleSort(col)}
										>
											{formatColumnHeader(col)}
										</TableSortLabel>
									</TableCell>
								))}
							</TableRow>
							{showFilters && (
								<TableRow>
									{columns.map(col => (
										<TableCell
											key={`filter-${col}`}
											sx={{ backgroundColor: 'grey.50', p: 0.5, top: 37 }}
										>
											<TextField
												size="small"
												variant="outlined"
												placeholder="Filter..."
												value={columnFilters[col] || ''}
												onChange={e => handleFilterChange(col, e.target.value)}
												slotProps={{
													input: {
														sx: { fontSize: '0.75rem', py: 0 },
														endAdornment: columnFilters[col] ? (
															<InputAdornment position="end">
																<IconButton
																	size="small"
																	onClick={() => handleFilterChange(col, '')}
																	edge="end"
																	sx={{ p: 0.25 }}
																>
																	<ClearIcon sx={{ fontSize: 14 }} />
																</IconButton>
															</InputAdornment>
														) : undefined,
													},
												}}
												sx={{ width: '100%', minWidth: 80 }}
											/>
										</TableCell>
									))}
								</TableRow>
							)}
						</TableHead>
						<TableBody>
							{processedResults.length === 0 ? (
								<TableRow>
									<TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
										<Typography variant="body2" color="text.secondary">
											No results match the current filters.
										</Typography>
									</TableCell>
								</TableRow>
							) : (
								processedResults.map((row, idx) => (
									<TableRow key={idx} hover>
										{columns.map(col => (
											<TableCell
												key={col}
												sx={{
													maxWidth: 300,
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap',
												}}
												title={formatCellValue(row[col])}
											>
												{formatCellValue(row[col])}
											</TableCell>
										))}
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
		</Box>
	);
};
